/**
 * Чистое ядро атрибуции: классификация касания, слияние с сохранённым состоянием,
 * кодек cookie и payload для бэкенда.
 *
 * Модуль намеренно не имеет импортов — он собирается одиночным вызовом tsc
 * и тестируется без окружения браузера. Всё, что требует window/document/cookie,
 * живёт в attribution-client.ts.
 */

export const ATTR_COOKIE_NAME = 'wgp_attr';
export const LEGACY_COOKIE_NAME = 'utm_params';
export const ATTR_COOKIE_DAYS = 90;

const MAX_UTM = 150;
const MAX_URL = 255;

const SEARCH_ENGINES: Array<[RegExp, string]> = [
  [/(^|\.)ya\.ru$/i, 'yandex'],
  [/(^|\.)yandex\./i, 'yandex'],
  [/(^|\.)google\./i, 'google'],
  [/(^|\.)mail\.ru$/i, 'mail'],
  [/(^|\.)rambler\.ru$/i, 'rambler'],
  [/(^|\.)bing\.com$/i, 'bing'],
  [/(^|\.)duckduckgo\.com$/i, 'duckduckgo'],
  [/(^|\.)yahoo\./i, 'yahoo'],
];

export interface Touch {
  source: string;
  medium: string;
  campaign: string;
  term: string;
  content: string;
  landing: string;
  referrer: string;
  ts: string | null;
}

export interface ClickIds {
  yclid: string;
  ysclid: string;
}

export interface Attribution {
  v: 1;
  first: Touch | null;
  last: Touch | null;
  click: ClickIds;
}

function clamp(value: unknown, max: number): string {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  return trimmed.length > max ? trimmed.slice(0, max) : trimmed;
}

function emptyTouch(): Touch {
  return { source: '', medium: '', campaign: '', term: '', content: '', landing: '', referrer: '', ts: null };
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./i, '').toLowerCase();
  } catch {
    return '';
  }
}

function isSameSite(refHost: string, host: string): boolean {
  const site = host.replace(/^www\./i, '').toLowerCase();
  return refHost === site || refHost.endsWith('.' + site) || site.endsWith('.' + refHost);
}

function searchEngineName(refHost: string): string {
  for (const [pattern, name] of SEARCH_ENGINES) {
    if (pattern.test(refHost)) return name;
  }
  return '';
}

/**
 * Возвращает касание или null. null означает «прямой заход или внутренний переход» —
 * такой визит не должен затирать ранее сохранённый рекламный источник
 * (правило last non-direct click).
 */
export function classifyTouch(input: {
  url: string;
  referrer: string;
  host: string;
  now?: string;
}): Touch | null {
  let parsed: URL;
  try {
    parsed = new URL(input.url);
  } catch {
    return null;
  }

  const q = parsed.searchParams;
  const ts = input.now || new Date().toISOString();
  const landing = clamp(parsed.pathname, MAX_URL);
  const refHost = hostOf(input.referrer);
  const external = refHost !== '' && !isSameSite(refHost, input.host);
  const referrer = external ? clamp(input.referrer, MAX_URL) : '';

  const utmSource = clamp(q.get('utm_source'), MAX_UTM);
  const utmMedium = clamp(q.get('utm_medium'), MAX_UTM);
  const utmCampaign = clamp(q.get('utm_campaign'), MAX_UTM);
  const utmTerm = clamp(q.get('utm_term'), MAX_UTM);
  const utmContent = clamp(q.get('utm_content'), MAX_UTM);
  const yclid = clamp(q.get('yclid'), MAX_UTM);
  const ysclid = clamp(q.get('ysclid'), MAX_UTM);

  const hasUtm = !!(utmSource || utmMedium || utmCampaign || utmTerm || utmContent);

  if (hasUtm || yclid || ysclid) {
    // Автометка Директа не кладёт в URL ни одной utm_*, только yclid.
    const source = utmSource || (yclid ? 'yandex' : ysclid ? 'yandex' : '');
    const medium = utmMedium || (yclid ? 'cpc' : ysclid ? 'organic' : '');
    return { source, medium, campaign: utmCampaign, term: utmTerm, content: utmContent, landing, referrer, ts };
  }

  if (external) {
    const engine = searchEngineName(refHost);
    const touch = emptyTouch();
    touch.source = engine || refHost;
    touch.medium = engine ? 'organic' : 'referral';
    touch.landing = landing;
    touch.referrer = referrer;
    touch.ts = ts;
    return touch;
  }

  return null;
}

export function mergeTouch(
  prev: Attribution | null,
  touch: Touch | null,
  click: { yclid?: string; ysclid?: string },
): Attribution {
  const base: Attribution =
    prev && prev.v === 1
      ? { v: 1, first: prev.first, last: prev.last, click: { yclid: prev.click.yclid, ysclid: prev.click.ysclid } }
      : { v: 1, first: null, last: null, click: { yclid: '', ysclid: '' } };

  if (!touch) return base;

  // Click IDs belong to the current non-direct touch. A later organic/referral
  // visit must not inherit an earlier paid click; direct returns keep both.
  base.click = {
    yclid: clamp(click.yclid, MAX_UTM),
    ysclid: clamp(click.ysclid, MAX_UTM),
  };
  base.last = touch;
  if (!base.first) base.first = touch;
  return base;
}

export function parseLegacyUtm(raw: string): Attribution | null {
  let data: Record<string, unknown>;
  try {
    data = JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return null;
  }
  if (!data || typeof data !== 'object') return null;

  const touch = emptyTouch();
  touch.source = clamp(data.utm_source, MAX_UTM);
  touch.medium = clamp(data.utm_medium, MAX_UTM);
  touch.campaign = clamp(data.utm_campaign, MAX_UTM);
  touch.term = clamp(data.utm_term, MAX_UTM);
  touch.content = clamp(data.utm_content, MAX_UTM);

  if (!touch.source && !touch.medium && !touch.campaign && !touch.term && !touch.content) return null;

  return { v: 1, first: touch, last: touch, click: { yclid: '', ysclid: '' } };
}

export function encodeAttribution(attr: Attribution): string {
  return JSON.stringify(attr);
}

export function decodeAttribution(raw: string | null | undefined): Attribution | null {
  if (!raw) return null;
  let data: any;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!data || data.v !== 1) return null;

  const norm = (t: any): Touch | null => {
    if (!t || typeof t !== 'object') return null;
    return {
      source: clamp(t.source, MAX_UTM),
      medium: clamp(t.medium, MAX_UTM),
      campaign: clamp(t.campaign, MAX_UTM),
      term: clamp(t.term, MAX_UTM),
      content: clamp(t.content, MAX_UTM),
      landing: clamp(t.landing, MAX_URL),
      referrer: clamp(t.referrer, MAX_URL),
      ts: typeof t.ts === 'string' ? t.ts : null,
    };
  };

  return {
    v: 1,
    first: norm(data.first),
    last: norm(data.last),
    click: {
      yclid: clamp(data.click && data.click.yclid, MAX_UTM),
      ysclid: clamp(data.click && data.click.ysclid, MAX_UTM),
    },
  };
}

/** Серверное чтение: в API routes доступен только заголовок Cookie. */
export function readAttributionFromCookieHeader(header: string | null): Attribution | null {
  if (!header) return null;

  const jar: Record<string, string> = {};
  const parts = header.split(';');
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    const idx = part.indexOf('=');
    if (idx <= 0) continue;
    const key = part.slice(0, idx).trim();
    const value = part.slice(idx + 1).trim();
    if (!key || !value) continue;
    try {
      jar[key] = decodeURIComponent(value);
    } catch {
      jar[key] = value;
    }
  }

  return decodeAttribution(jar[ATTR_COOKIE_NAME]) || parseLegacyUtm(jar[LEGACY_COOKIE_NAME] || '');
}

/** Плоский набор полей, который принимают эндпоинты Bitrix. Все значения — строки. */
export function toBackendPayload(attr: Attribution | null): Record<string, string> {
  const last = (attr && attr.last) || emptyTouch();
  const first = (attr && attr.first) || emptyTouch();
  // Older cookies could retain yclid after a different last touch. Do not send
  // that stale paid identifier alongside an organic, referral or other source.
  const source = last.source.toLowerCase();
  const medium = last.medium.toLowerCase();
  const yandexSource = source === '' || ['yandex', 'yandex_direct', 'yandex-direct', 'direct', 'ya', 'ya.ru'].includes(source);
  const paidMedium = medium === '' || ['cpc', 'ppc', 'paid', 'banner', 'display', 'cpm'].includes(medium);
  return {
    utm_source: last.source,
    utm_medium: last.medium,
    utm_campaign: last.campaign,
    utm_term: last.term,
    utm_content: last.content,
    first_utm_source: first.source,
    first_utm_medium: first.medium,
    first_utm_campaign: first.campaign,
    first_touch_at: first.ts || '',
    landing_page: last.landing,
    referrer: last.referrer,
    yclid: yandexSource && paidMedium ? (attr && attr.click.yclid) || '' : '',
  };
}
